import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import apiService from '../../services/api';
import KpiRow, { Kpi } from './KpiRow';
import { EmployeeOption, Avatar, rupee, hasSalarySetup } from './shared';

interface AdminUser {
  id: number;
  name?: string;
  email?: string;
  phone?: string | null;
  role?: string;
}

interface Props {
  employees: EmployeeOption[];
  isAdmin: boolean;
  onEdit: (emp: EmployeeOption) => void;
  onDelete: (emp: EmployeeOption) => void;
}

// Human-readable pay rule for the People table.
const payRule = (e: EmployeeOption): string => {
  if (!hasSalarySetup(e)) return 'No pay rule';
  const unit = e.extraUnit === 'HOUR' ? 'hour' : 'machine';
  const extra = (e.extraRate || 0) > 0 ? ` + ${rupee(e.extraRate || 0)} / extra ${unit}` : '';
  if (e.type === 'METER') return `Per-meter rate${extra}`;
  return `${rupee(e.rate || 0)} base${extra}`;
};

const roleName = (e: EmployeeOption): string =>
  e.user?.userRole?.name || e.user?.role || 'EMPLOYEE';

const ApprovesBadge: React.FC<{ on: boolean }> = ({ on }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${on ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
    {on ? 'YES' : 'NO'}
  </span>
);

const HEAD = ['Name', 'Email', 'Pay rule', 'Role', 'Approves', 'Actions'];

const GroupCard: React.FC<{
  title: string;
  note: string;
  rows: React.ReactNode;
  count: number;
}> = ({ title, note, rows, count }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-x-3 gap-y-1">
      <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
      <span className="text-xs font-medium text-gray-500">{count}</span>
      <span className="text-xs text-gray-500">{note}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50/60">
          <tr>
            {HEAD.map((h) => (
              <th key={h} className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">{rows}</tbody>
      </table>
    </div>
  </div>
);

const PeopleTab: React.FC<Props> = ({ employees, isAdmin, onEdit, onDelete }) => {
  // Owners & office come from the admin list (may be scoped/forbidden — swallow and show empty).
  const { data: adminsData } = useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => apiService.get('/admin/admins'),
    enabled: isAdmin,
    retry: false,
  });
  const admins: AdminUser[] = useMemo(() => {
    const d: any = adminsData;
    const list = Array.isArray(d) ? d : (d?.admins ?? d?.data ?? []);
    return (list as AdminUser[]).filter((a) => a && (a.role === 'ADMIN' || a.role === 'SUPERADMIN'));
  }, [adminsData]);

  const floor = employees.filter(hasSalarySetup);
  const supervision = employees.filter((e) => !hasSalarySetup(e) && e.canApproveAttendance);
  const office = employees.filter((e) => !hasSalarySetup(e) && !e.canApproveAttendance);

  const kpis: Kpi[] = [
    { label: 'Headcount', value: employees.length + admins.length, note: `${floor.length} on floor, ${employees.length - floor.length + admins.length} office` },
    { label: 'Can approve', value: employees.filter((e) => e.canApproveAttendance).length + admins.length, note: 'admins + trusted managers' },
    { label: 'On floor payroll', value: floor.length, note: 'attendance tracked' },
    { label: 'No pay rule', value: office.length + supervision.length, note: 'not on floor payroll', tone: (office.length + supervision.length) > 0 ? 'warn' : 'default' },
  ];

  const empRow = (emp: EmployeeOption) => (
    <tr key={emp.id} className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <Avatar name={emp.user?.name} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{emp.user?.name || `Employee #${emp.id}`}</div>
            {emp.designation && <div className="text-xs text-gray-500 truncate">{emp.designation}</div>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{emp.user?.email || '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{payRule(emp)}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{roleName(emp)}</td>
      <td className="px-4 py-3"><ApprovesBadge on={!!emp.canApproveAttendance} /></td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => onEdit(emp)} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
          <button onClick={() => onDelete(emp)} className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-red-600">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      </td>
    </tr>
  );

  const adminRow = (a: AdminUser) => (
    <tr key={`a${a.id}`} className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <Avatar name={a.name} />
          <div className="text-sm font-medium text-gray-900 truncate">{a.name || `User #${a.id}`}</div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{a.email || '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-400">—</td>
      <td className="px-4 py-3 text-sm text-gray-700">{a.role || 'ADMIN'}</td>
      <td className="px-4 py-3"><ApprovesBadge on /></td>
      <td className="px-4 py-3 text-sm text-gray-300">Manage in Companies</td>
    </tr>
  );

  const emptyRow = (label: string) => (
    <tr><td colSpan={HEAD.length} className="px-4 py-8 text-center text-gray-400 text-sm">{label}</td></tr>
  );

  return (
    <div className="space-y-5">
      <KpiRow items={kpis} />

      <GroupCard
        title="Production floor"
        count={floor.length}
        note="Clock in and out · attendance tracked"
        rows={floor.length ? floor.map(empRow) : emptyRow('No floor staff with a pay rule yet.')}
      />
      <GroupCard
        title="Supervision"
        count={supervision.length}
        note="Eligible to approve clock-ins on your behalf"
        rows={supervision.length ? supervision.map(empRow) : emptyRow('Nobody flagged as an approver (outside the floor).')}
      />
      {(office.length > 0) && (
        <GroupCard
          title="Office & other"
          count={office.length}
          note="No floor payroll · no attendance tracked"
          rows={office.map(empRow)}
        />
      )}
      {admins.length > 0 && (
        <GroupCard
          title="Owners & office"
          count={admins.length}
          note="Full visibility · no attendance tracked"
          rows={admins.map(adminRow)}
        />
      )}
    </div>
  );
};

export default PeopleTab;
